import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../../../components/modern/PageLayout/PageLayout';
import ActionButton from '../../../components/modern/ActionButton/ActionButton';
import { MultiLanguageTextInput } from '../../../components/modern';
import { adminApi } from '../../../utils/api';
import './CreateCity.scss';

interface Country {
  id: number;
  name: string;
}

const CreateCity: React.FC = () => {
  const { t } = useTranslation(['cities', 'common']);
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [countryId, setCountryId] = useState<number>(0);
  const [localDeliveryFee, setLocalDeliveryFee] = useState<number>(0);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      const response = await adminApi.call('/country?page=1&pageSize=1000', 'GET');

      if (!response.success) {
        setError(response.error || t('messages.fetchCountriesError'));
        return;
      }

      setCountries(response.data?.items || []);
    } catch (error) {
      console.error('Error fetching countries:', error);
      setError(t('messages.fetchCountriesError'));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};
    if (!name.trim()) newErrors.name = t('cities:validation.nameRequired');
    if (!countryId) newErrors.countryId = t('cities:validation.countryRequired');
    if (localDeliveryFee < 0) newErrors.localDeliveryFee = t('cities:validation.deliveryFeeRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      setError(t('cities:validation.fillAllFields'));
      return;
    }

    setLoading(true);
    setError(null);
    setErrors({});

    if (!countryId) {
      setError(t('validation.countryRequired'));
      setLoading(false);
      return;
    }

    try {
      const response = await adminApi.call('/city', 'POST', {
        name: name.trim(),
        countryId: countryId,
        localDeliveryFee: localDeliveryFee,
      });

      if (!response.success) {
        setError(response.error || t('messages.createError'));
        return;
      }

      navigate('/cities');
    } catch (error) {
      console.error('Error creating city:', error);
      setError(t('messages.createError'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setName('');
    setCountryId(0);
    setLocalDeliveryFee(0);
    setError(null);
  };

  return (
    <PageLayout 
      title={t('create.title')} 
      breadcrumbs={[
        { label: t('title'), path: '/cities' },
        { label: t('create.title') }
      ]}
    >
      <div className="create-city">
        <div className="card">
          <div className="card-body">
            {error && (
              <div className="alert alert-danger" role="alert">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6">
                  <MultiLanguageTextInput
                    label={t('fields.name')}
                    value={name}
                    onChange={(value) => {
                      setName(value);
                      if (errors.name) {
                        setErrors(prev => ({ ...prev, name: undefined }));
                      }
                    }}
                    placeholder={t('placeholders.enterName')}
                    error={errors.name}
                    disabled={loading}
                    required
                  />
                </div>

                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label">{t('fields.country')}</label>
                    <select
                      className="form-control"
                      value={countryId}
                      onChange={(e) => setCountryId(Number(e.target.value))}
                      required
                    >
                      <option value="">{t('placeholders.selectCountry')}</option>
                      {countries.map((country) => (
                        <option key={country.id} value={country.id}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label">{t('fields.localDeliveryFee')}</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="form-control"
                      value={localDeliveryFee}
                      onChange={(e) => setLocalDeliveryFee(Number(e.target.value))}
                      placeholder={t('placeholders.enterLocalDeliveryFee')}
                    />
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <ActionButton
                  type="submit"
                  variant="primary"
                  loading={loading}
                  disabled={loading}
                >
                  {t('actions.create')}
                </ActionButton>
               
                <ActionButton
                  type="button"
                  variant="outline-secondary"
                  onClick={() => navigate('/cities')}
                  disabled={loading}
                >
                  {t('cities:actions.cancel')}
                </ActionButton>
              </div>
            </form>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default CreateCity;
